import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RemoveIcon from "@mui/icons-material/Remove";
import { LoadingButton } from "@mui/lab";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Link as MuiLink,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useRecoilState } from "recoil";
import {
  useAddSalaryEmployeeAttachment,
  useCreateSalaryEmployee,
  useGetSalaryEmployee,
  useDeleteSalaryEmployeeAttachment,
  useUpdateSalaryEmployee,
} from "src/api/salary/employees";
import { notificationsState } from "src/store/notifications";
import { APIErrorResponse } from "src/types/generic";
import {
  employeeFormDefaultValues,
  employeeFormSchema,
  EmployeeFormValues,
} from "./constants";
import FilePondUploader from "src/shared/components/file-pond-uploader";

const sectionTitleSx = {
  fontSize: "0.875rem",
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase" as const,
  color: "#667085",
};

const EmployeeFormPage = () => {
  const { t } = useTranslation("salary");
  const { t: generalT } = useTranslation("general");
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [attachmentPondKey, setAttachmentPondKey] = useState(0);

  const { id } = useParams();
  const employeeId = id ? Number(id) : null;
  const mode: "add" | "edit" | "view" = !id
    ? "add"
    : /\/edit\/?$/.test(pathname)
      ? "edit"
      : "view";

  const isAdd = mode === "add";
  const isEdit = mode === "edit";
  const isView = mode === "view";
  const readOnly = isView;

  const { data: employeeRes, isLoading: loadingDetail } = useGetSalaryEmployee(
    employeeId,
    isEdit || isView,
  );
  const detail = employeeRes?.data;

  const { mutate: createMut, isPending: creating } = useCreateSalaryEmployee();
  const { mutate: updateMut, isPending: updating } = useUpdateSalaryEmployee();
  const { mutateAsync: addAttachmentAsync, isPending: uploadingAttachment } =
    useAddSalaryEmployeeAttachment();
  const { mutateAsync: deleteAttachmentAsync, isPending: deletingAttachment } =
    useDeleteSalaryEmployeeAttachment();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<EmployeeFormValues>({
    mode: "onBlur",
    resolver: yupResolver(employeeFormSchema),
    defaultValues: employeeFormDefaultValues,
  });

  useEffect(() => {
    if (isAdd) {
      reset(employeeFormDefaultValues);
      return;
    }
    if (!detail) return;
    reset({
      name: detail.name,
      start_balance: detail.start_balance,
      description: detail.description ?? "",
      allowances:
        detail.allowances?.map((a: any) => ({
          id: a.id,
          name: a.name,
          amount: a.amount,
        })) ?? [],
    });
  }, [isAdd, detail, reset]);

  const pushError = (err: APIErrorResponse) => {
    const msg = err?.data?.errors?.[0]?.value ?? generalT("somethingWrong");
    setNotifications([...notifications, { type: "error", message: msg }]);
  };

  const uploadPending = async (newEmployeeId: number) => {
    for (const file of pendingFiles) {
      await addAttachmentAsync({ employeeId: newEmployeeId, file });
    }
    setPendingFiles([]);
    setAttachmentPondKey((k) => k + 1);
  };

  const { fields: allowanceFields, append: appendAllowance, remove: removeAllowance } = useFieldArray({
    name: "allowances",
    control,
  });

  const onSubmit: SubmitHandler<EmployeeFormValues> = (values) => {
    const allowances = (values.allowances ?? [])
      .filter((a) => a.name?.trim())
      .map((a) => ({
        id: a.id,
        name: a.name!.trim(),
        amount: Number(a.amount) || 0,
      }));

    const payload = {
      name: values.name,
      start_balance: Number(values.start_balance),
      description: values.description || null,
      allowances,
    };

    if (isEdit && employeeId) {
      updateMut(
        { id: employeeId, ...payload },
        {
          onSuccess: async () => {
            try {
              if (pendingFiles.length) {
                await uploadPending(employeeId);
              }
            } catch (e) {
              pushError(e as APIErrorResponse);
              return;
            }
            setNotifications([
              ...notifications,
              { type: "success", message: generalT("updatedSuccessfully") },
            ]);
            navigate("/salary/employees");
          },
          onError: pushError,
        },
      );
    } else {
      createMut(
        { ...payload, status: true },
        {
          onSuccess: async (res: unknown) => {
            const body = res as { data?: { id?: number } };
            const newId = body?.data?.id;
            if (newId && pendingFiles.length) {
              try {
                await uploadPending(newId);
              } catch {
                setNotifications([
                  ...notifications,
                  { type: "success", message: generalT("addedSuccessfully") },
                  {
                    type: "error",
                    message: t("attachmentsUploadPartial"),
                  },
                ]);
                navigate("/salary/employees");
                return;
              }
            }
            setNotifications([
              ...notifications,
              { type: "success", message: generalT("addedSuccessfully") },
            ]);
            navigate("/salary/employees");
          },
          onError: pushError,
        },
      );
    }
  };

  const goBack = () => navigate("/salary/employees");


  const existingAttachments =
    detail?.files?.filter((a: any) => !!a?.file?.url && !!a?.file?.name) ?? [];

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#FAFAFA" }}>
      <Box
        sx={{
          borderBottom: "1px solid #EAECF0",
          bgcolor: "#fff",
        }}
      >
        <Box
          sx={{
            maxWidth: 768,
            mx: "auto",
            px: { xs: 2, sm: 3 },
            py: 2,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <IconButton
            onClick={goBack}
            size="small"
            sx={{
              borderRadius: 1,
              color: "#101828",
              "&:hover": { bgcolor: "action.hover" },
            }}
            aria-label={generalT("back")}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography component="h1" fontSize={18} fontWeight={600} color="#101828">
            {isEdit ? t("editEmployee") : isView ? t("viewEmployee") : t("addEmployee")}
          </Typography>
        </Box>
      </Box>

      <Box
        component="form"
        onSubmit={readOnly ? (e) => e.preventDefault() : handleSubmit(onSubmit)}
        sx={{ maxWidth: 768, mx: "auto", px: { xs: 2, sm: 3 }, py: 4 }}
      >
        <Stack spacing={5}>
          {isEdit && loadingDetail ? (
            <Typography color="#98A2B3">{generalT("loading")}…</Typography>
          ) : (
            <>
              <Box component="section">
                <Typography sx={sectionTitleSx} mb={2.5}>
                  {t("basicInformation")}
                </Typography>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="name"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          id="emp-name"
                          label={t("employeeName")}
                          required
                          fullWidth
                          size="small"
                          placeholder={t("employeeNamePlaceholder")}
                          disabled={readOnly}
                          error={!!errors.name}
                          helperText={
                            errors.name ? generalT(errors.name.message as string) : undefined
                          }
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="start_balance"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          id="start-balance"
                          label={t("basicSalary")}
                          required
                          type="number"
                          fullWidth
                          size="small"
                          disabled={readOnly}
                          error={!!errors.start_balance}
                          helperText={
                            errors.start_balance
                              ? generalT(errors.start_balance.message as string)
                              : undefined
                          }
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Box component="section">
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={2}
                >
                  <Typography sx={sectionTitleSx}>{t("allowances")}</Typography>
                  {!readOnly && (
                    <Button
                      type="button"
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => appendAllowance({ name: "", amount: 0 })}
                      sx={{ textTransform: "none", fontWeight: 500 }}
                    >
                      {t("addAllowance")}
                    </Button>
                  )}
                </Stack>
                {allowanceFields.length === 0 ? (
                  <Box
                    sx={{
                      border: "1px dashed #D0D5DD",
                      borderRadius: 2,
                      p: 3,
                      textAlign: "center",
                      color: "#667085",
                      fontSize: 14,
                    }}
                  >
                    {t("noAllowancesYet")}
                  </Box>
                ) : (
                  <Stack spacing={2}>
                    {allowanceFields.map((field, index) => (
                      <Box
                        key={field.id}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            sm: readOnly ? "1fr 160px" : "1fr 160px 40px",
                          },
                          gap: 1.5,
                          alignItems: "start",
                          width: "100%",
                        }}
                      >
                        <Controller
                          name={`allowances.${index}.name`}
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              size="small"
                              label={t("allowanceName")}
                              required
                              disabled={readOnly}
                              error={!!errors.allowances?.[index]?.name}
                              helperText={
                                errors.allowances?.[index]?.name
                                  ? generalT(errors.allowances[index]!.name!.message as string)
                                  : ""
                              }
                            />
                          )}
                        />
                        <Controller
                          name={`allowances.${index}.amount`}
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              size="small"
                              type="number"
                              label={t("allowanceAmount")}
                              required
                              disabled={readOnly}
                              error={!!errors.allowances?.[index]?.amount}
                              helperText={
                                errors.allowances?.[index]?.amount
                                  ? generalT(errors.allowances[index]!.amount!.message as string)
                                  : ""
                              }
                            />
                          )}
                        />
                        {!readOnly && (
                          <IconButton
                            size="small"
                            onClick={() => removeAllowance(index)}
                            sx={{ mt: 0.5 }}
                          >
                            <RemoveIcon />
                          </IconButton>
                        )}
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>

              <Box component="section">
                <Typography sx={sectionTitleSx} mb={2}>
                  {generalT("description")}
                </Typography>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value ?? ""}
                      fullWidth
                      multiline
                      minRows={4}
                      placeholder={t("descriptionPlaceholder")}
                      size="small"
                      disabled={readOnly}
                    />
                  )}
                />
              </Box>

              <Box component="section">
                <Typography sx={sectionTitleSx} mb={2}>
                  {generalT("attachments")}
                </Typography>

                {existingAttachments.length > 0 ? (
                  <Stack spacing={1.25} mb={2}>
                    {existingAttachments.map((a: any) => (
                      <Stack
                        key={a.id}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={2}
                        sx={{
                          border: "1px solid #EAECF0",
                          borderRadius: 1.5,
                          px: 2,
                          py: 1.25,
                          bgcolor: "#fff",
                        }}
                      >
                        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                          <MuiLink
                            href={a.file!.url}
                            target="_blank"
                            rel="noreferrer"
                            underline="hover"
                            sx={{
                              color: "#101828",
                              fontWeight: 600,
                              fontSize: 14,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={a.file!.name}
                          >
                            {a.file!.name}
                          </MuiLink>
                          <Typography variant="caption" color="text.secondary">
                            {a.file?.extension
                              ? a.file.extension.toUpperCase()
                              : ""}
                            {a.file?.size ? ` • ${(a.file.size / 1024).toFixed(0)} KB` : ""}
                          </Typography>
                        </Stack>

                        {!readOnly && (
                          <IconButton
                            size="small"
                            aria-label={t("deleteEmployee")}
                            disabled={!employeeId || deletingAttachment}
                            onClick={async () => {
                              if (!employeeId) return;
                              try {
                                await deleteAttachmentAsync({
                                  employeeId,
                                  employeeFileId: a.id,
                                });
                                setNotifications([
                                  ...notifications,
                                  {
                                    type: "success",
                                    message: generalT("deletedSuccessfully"),
                                  },
                                ]);
                              } catch (e) {
                                pushError(e as APIErrorResponse);
                              }
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Stack>
                    ))}
                  </Stack>
                ) : (
                  <Typography color="text.secondary" fontSize={14} mb={2}>
                    {t("noAttachments")}
                  </Typography>
                )}

                {!readOnly && (
                  <FilePondUploader
                    key={attachmentPondKey}
                    onUpload={setPendingFiles}
                    maxFiles={3}
                    maxFileSize={3}
                    acceptedFileTypes={[
                      "image/*",
                      "application/pdf",
                      "text/csv",
                      "application/vnd.ms-excel",
                      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    ]}
                  />
                )}
              </Box>
            </>
          )}
        </Stack>

        <Stack
          direction="row"
          justifyContent="flex-end"
          gap={1.5}
          sx={{ mt: 5, pt: 3, borderTop: "1px solid #EAECF0" }}
        >
          <Button
            variant={readOnly ? "contained" : "outlined"}
            type="button"
            onClick={goBack}
            sx={{ textTransform: "none" }}
          >
            {readOnly ? t("close") : generalT("cancel")}
          </Button>
          {!readOnly && (
            <LoadingButton
              type="submit"
              variant="contained"
              loading={creating || updating || uploadingAttachment}
              disabled={isEdit && loadingDetail}
              sx={{ textTransform: "none" }}
            >
              {t("saveEmployee")}
            </LoadingButton>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default EmployeeFormPage;
